<template>
  <div data-component-group="threads-form" data-component="merchant">
    <slot name="beforeForm" />

    <v-autocomplete
      v-model="merchant"
      :items="merchants"
      v-model:search="merchantSearch"
      prepend-inner-icon="store"
      label="Merchant"
      item-title="name"
      item-value="name"
      :hide-details="false"
      return-object
      required
      :loading="isLoadingMerchants"
      :readonly="isDisabled"
      :error-messages="merchantErrors"
    />

    <div v-if="!isDisabled" class="component-actions">
      <v-btn color="secondary" :loading="isLoading" @click="submit">
        <v-icon>edit</v-icon>
        {{ triggerLabel }}
      </v-btn>
    </div>

    <slot name="afterForm"/>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required } from '@vuelidate/validators';
  import { v1 as uuidv1 } from 'uuid';
  import { isNum, isStr, isObj, debounce } from '../../../utils';

  const debouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  export default {
    setup () {
    return {
      v$: useVuelidate()
    }
  },
    validations() {
      return {
        merchant: {
          required,
        },
      };
    },
    props: {
      thread: {
        type: Object,
        default: undefined,
      },
      triggerLabel: {
        type: String,
        default: 'Update',
      },
      isLoading: {
        type: Boolean,
        default: false,
      },
      isDisabled: {
        type: Boolean,
        default: false,
      },
      onSubmit: {
        type: Function,
        default: () => {},
      },
    },
    data() {
      return {
        merchant: null,
        merchants: [],
        merchantSearch: '',
        isLoadingMerchants: false,
        isRefreshingMerchants: false,
      };
    },
    computed: {
      merchantErrors() {
        return this.v$.merchant.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Merchant is required';
          }
          return error.$message;
        });
      },
    },
    watch: {
      thread(nextThread) {
        if (isObj(nextThread)) {
          this.clear();

          this.setMerchantsData(nextThread);
          this.isRefreshingMerchants = true;
        }
      },
      merchant() {
        this.isRefreshingMerchants = true;
      },
      merchantSearch(nextSearch) {
        if (isStr(nextSearch)) {
          if (this.isRefreshingMerchants) {
            this.isRefreshingMerchants = false;
            return false;
          }

          debouncer(() => this.searchMerchants(nextSearch));
        }
      },
    },
    created() {
      if (isObj(this.thread)) {
        this.setMerchantsData(this.thread);
      }
    },
    methods: {
      setMerchantsData(thread) {
        if (isObj(thread.merchant)) {
          this.merchant = thread.merchant;
          this.merchants = [this.merchant];
        }
      },
      searchMerchants(search) {
        const query = {
          id: this.thread.id,
          search,
        };

        this.isLoadingMerchants = true;

        if (!this.$ability.can('account-manager-store', 'thread')) {
          return;
        }

        return this.$store
          .dispatch('threads/searchExternalMerchants', query)
          .then((res) => {
            this.merchants = res.data.data;
            if (this.merchants.length === 0 && isObj(this.merchant)) {
              this.merchants = [this.merchant]; // if merchants array is empty, selected option dissapears
            }

            return res;
          })
          .finally(() => {
            this.isLoadingMerchants = false;
          });
      },
      submit() {
        this.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.$error) {
          return false;
        }

        const { merchant, onSubmit } = this;
        return onSubmit(merchant);
      },
      canSubmitRequest() {
        return isNum(this.merchant?.id);
      },
      clear() {
        this.merchant = null;
        this.merchants = [];
        this.merchantSearch = '';
        this.isRefreshingMerchants = false;
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='threads-form'][data-component='merchant'] {
  padding: $spacing-xl;
  background-color: map.get($colors, shades, 'white');
  border-top: 1px solid map.get($colors, grey, lighten-2);

  form {
    border-radius: $border-radius;
  }

  .component-actions {
    text-align: right;

    .v-btn {
      margin: 0;

      .v-icon {
        margin: 0 6px 0 0;
      }
    }
  }

  .merchants {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
  }

  .label {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    font-size: $text-size-sm;
    padding: 2px 6px;
    margin: 0 10px 10px 0;
    border-radius: $border-radius;
    color: map.get($colors, shades, 'white');
    background-color: map.get($colors, primary);

    .v-icon {
      font-size: $text-size-md;
      margin: 0 6px 0 0;
      color: map.get($colors, shades, 'white');
    }
  }

  .v-input {
    .v-select__selection {
      color: map.get($colors, shades, 'white');
      background-color: map.get($colors, primary);
      border-radius: $border-radius;
      padding: 5px;
    }

    &.v-input--is-disabled {
      .v-select__selection {
        color: map.get($colors, shades, 'white');
        background-color: map.get($colors, grey, base);
        border-radius: $border-radius;
        padding: 5px;
      }
    }
  }
}
</style>
